import { FlexBox, Text, Title } from "@smoothincorg/smooth-ui"
import dayjs from "dayjs"
import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { BorderedButton } from "src/components/Button"
import { Card } from "src/components/page-directory/Card"
import { Wrapper } from "src/components/page-directory/Wrapper"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { useForm } from "react-hook-form"
import { Radios } from "src/components/Radios"
import { InputField } from "src/components/InputField"
import Head from "next/head"
import { convert } from "encoding-japanese"

type FetchData = {
  data: string[]
  errors: string[]
}

type Props = {
  className?: string
  errors?: string[]
  formState: {
    mode: string
    filterType: string
  }
  setFormState: React.Dispatch<React.SetStateAction<Props["formState"]>>
  handleSubmit: (payload: {
    reconciliationToDateAgainstPaymentAt: string
    contractIds: string
    preview?: boolean
  }) => Promise<void>

  // previewJson?: Record<string, any>
  previewJson?: FetchData
  fetchCount: number
  totalCount: number
  estimateRemain: string
}

// eslint-disable-next-line react/display-name
const Component = React.memo<Props>((props) => {
  const { register, handleSubmit, setValue } = useForm({
    defaultValues: {
      preview: false,
      contractIds: "",
      reconciliationToDateAgainstPaymentAt: dayjs().date(20).format("YYYY-MM-DD"),
    },
  })

  return (
    <div className={props.className}>
      <Head>
        <title>CIC{props.formState?.mode === "update" ? "更新" : "登録"} | for CS</title>
      </Head>
      <Wrapper>
        <form onSubmit={handleSubmit(props.handleSubmit)}>
          <FlexBox justifyContent="space-between">
            <div style={{ width: "100%" }}>
              <Card>
                <section>
                  <Title as="h2" size={14}>
                    モード：
                  </Title>
                  <Radios
                    id="mode"
                    options={[
                      { name: "登録", value: "register" },
                      { name: "更新", value: "update" },
                    ]}
                    value={props.formState.mode}
                    onChange={(e) => {
                      props.setFormState({
                        ...props.formState,
                        mode: e.target.value,
                      })
                    }}
                  />
                </section>
                <section>
                  <Title as="h2" size={14}>
                    フィルターの種類：
                  </Title>
                  <Radios
                    id="filter_type"
                    options={[
                      // { name: "除外", value: "remove" },
                      { name: "対象", value: "include" },
                    ].filter(({ value }) => {
                      if (props.formState.mode === "register") {
                        return value === "register"
                      }

                      return true
                    })}
                    value={props.formState.filterType}
                    onChange={(e) => {
                      props.setFormState({
                        ...props.formState,
                        filterType: e.target.value,
                      })
                    }}
                  />
                </section>
              </Card>
            </div>
          </FlexBox>

          <Card>
            <section>
              <InputField
                type="text"
                label={`
                  ${
                    props.formState.filterType === "remove" ? "除外" : "対象に"
                  }するCONTRACT_ID（カンマ区切りで）：`}
                {...register("contractIds")}
              />
            </section>
            {props.formState.mode === "update" && (
              <section>
                <InputField
                  type="text"
                  label="支払い(Reconciliation)の対象範囲を下記日程まで含める（初期値は当月20日）："
                  defaultValue={dayjs().date(20).format("YYYY-MM-DD")}
                  {...(register("reconciliationToDateAgainstPaymentAt"),
                  {
                    defaultValue: dayjs().date(20).format("YYYY-MM-DD"),
                  })}
                  onChange={(e) => setValue("reconciliationToDateAgainstPaymentAt", e.target.value)}
                />
              </section>
            )}
            {/* 取得予想時間/数を表示 */}
            <div>
              {props.fetchCount}/{props.totalCount}{" "}
              {props.estimateRemain === "" ? "" : `(残り予想時間:${props.estimateRemain})秒`}
            </div>

            <input type="hidden" value="remove" name="filter_type" />
            <BorderedButton
              type="submit"
              onClick={() => {
                setValue("preview", false)
              }}
            >
              [{props.formState.mode === "register" ? "登録" : "更新"}] cicのcsv吐き出す
            </BorderedButton>
            <BorderedButton
              type="submit"
              onClick={() => {
                setValue("preview", true)
              }}
            >
              [{props.formState.mode === "register" ? "登録" : "更新"}] プレビュー
            </BorderedButton>
            {props.errors?.map((error) => (
              <Text key={error} color="red">
                {error}
              </Text>
            ))}
          </Card>
        </form>

        {/* プレビューに関係なく表示する仕様に変更 */}
        <div style={{ width: "100%", margin: "20px auto" }}>
          <Card>
            <Title as="h2" size={14}>
              プレビュー：
            </Title>
            <div>
              成功:
              {props.previewJson.data &&
                // props.previewJson.data.split("\n").filter((s: string) => s).length}
                props.previewJson.data.filter((s: string) => s).length}
              件
            </div>
            <div>
              エラー:
              {props.previewJson.errors.length}件
              <ul>
                {props.previewJson.errors.map((error: string) => (
                  <li
                    key={error}
                    style={{
                      color: "red",
                    }}
                  >
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </Wrapper>
      {props.previewJson && (
        <div
          style={{
            margin: "20px auto",
            maxWidth: "3000px",
            width: "90%",
          }}
        >
          <Card>
            <Title as="h2" size={14}>
              プレビューCSV：
            </Title>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                overflow: "scroll",
              }}
            >
              {props.previewJson.data}
            </pre>
          </Card>
        </div>
      )}
    </div>
  )
})

const StyledComponent = styled(Component)`
  padding: 20px 0;

  > div {
    .smooth-ui-Card {
      padding: 20px;
      margin-bottom: 16px;

      > section {
        margin-bottom: 18px;
      }
    }
  }
`

export const FetchCicCsv = () => {
  const { postWithToken } = useSmoothApi()
  const [formState, setFormState] = useState({
    mode: "register",
    filterType: "remove",
  })
  const anchorRef = useRef<HTMLAnchorElement>(null)
  // const [previewJson, setPreviewJson] = useState<Record<string, string>>(null)
  const [previewJson, setPreviewJson] = useState<FetchData>({
    data: [],
    errors: [],
  })
  //
  const [fetchCount, setFetchCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [fetchTimes, setFetchTimes] = useState([])
  const [bufferedTime, setBufferedTime] = useState("")
  const [estimateRemain, setEstimateRemain] = useState("")

  useEffect(() => {
    // 残り取得予想時間の管理
    if (fetchTimes.length === 0 || totalCount === 0) return
    const ave = fetchTimes.reduce((a, b) => a + b, 0) / fetchTimes.length
    const remainReq = totalCount - fetchTimes.length
    const estimateTime = (ave * remainReq).toFixed(2)
    setBufferedTime(estimateTime)
  }, [fetchTimes, totalCount])

  useEffect(() => {
    // 0.1sごとに更新
    const intervalId = setInterval(() => {
      setEstimateRemain(bufferedTime)
    }, 100)
    return () => clearInterval(intervalId)
  }, [bufferedTime])

  // 一括取得
  const handleSubmit = async (payload: {
    reconciliationToDateAgainstPaymentAt: string
    contractIds: string
    preview?: boolean
  }) => {
    const formData = new FormData()
    if (payload.contractIds) {
      formData.append("contract_ids", payload.contractIds)
    }

    formData.append(
      "reconciliation_to_date_against_payment_at",
      payload.reconciliationToDateAgainstPaymentAt
    )
    formData.append("filter_type", formState.filterType)

    const endPoint =
      formState.mode === "register" ? "generate_cic_csv_for_create" : "generate_cic_csv_for_update"

    if (payload.preview) {
      const res = await postWithToken(
        `${SMOOTH_SMOOTHER_API_URL}/for_cic/` + endPoint + "?preview=true",
        formData,
        {
          responseType: "json",
          timeout: 1000 * 60 * 30,
        }
      )
      setPreviewJson(res.data)
      return
    }

    const res = await postWithToken(`${SMOOTH_SMOOTHER_API_URL}/for_cic/` + endPoint, formData, {
      responseType: "arraybuffer",
      timeout: 1000 * 60 * 30,
    })

    const arr = new Uint8Array(res.data as ArrayBuffer)
    const data = convert(arr, "SJIS")
    const blob = new Blob([new Uint8Array(data)], {
      type: "text/csv;charset=shift_jis;",
    })
    const url = URL || webkitURL
    anchorRef.current.href = url.createObjectURL(blob)
    anchorRef.current.download = `${dayjs().format("YYY-MM-DD_HHmm")}.csv`
    anchorRef.current.click()
  }

  // 取得部分のみ切り出し
  const fetcher = async (payload: {
    reconciliationToDateAgainstPaymentAt: string
    contractIds: string
    preview?: boolean
    index: number
  }) => {
    const formData = new FormData()
    if (payload.contractIds) {
      formData.append("contract_ids", payload.contractIds)
    }

    formData.append(
      "reconciliation_to_date_against_payment_at",
      payload.reconciliationToDateAgainstPaymentAt
    )
    formData.append("filter_type", formState.filterType)

    const endPoint =
      formState.mode === "register" ? "generate_cic_csv_for_create" : "generate_cic_csv_for_update"

    const start = performance.now()

    const res = await postWithToken(
      `${SMOOTH_SMOOTHER_API_URL}/for_cic/` + endPoint + "?preview=true",
      formData,
      {
        responseType: "json",
        // タイムアウト処理でエラーになったものはエラー処理とする
        // timeout: 1000 * 60 * 30,
      }
    )
    const end = performance.now()

    const elapsed = ((end - start) / 1000).toFixed(2)
    setFetchTimes((prevTimes) => [...prevTimes, Number(elapsed)])

    return res
  }

  // 分割取得
  const splitTransmission = async (payload: {
    reconciliationToDateAgainstPaymentAt: string
    contractIds: string
    preview?: boolean
  }) => {
    const cIdsAry = payload.contractIds.split(",")

    const results: FetchData = {
      data: [],
      errors: [],
    }
    setFetchCount(0)
    setTotalCount(cIdsAry.length)
    setFetchTimes([])
    setPreviewJson({
      data: [],
      errors: [],
    })

    let i = 1
    for (const [index, cid] of cIdsAry.entries()) {
      const { reconciliationToDateAgainstPaymentAt, preview } = payload
      await fetcher({
        reconciliationToDateAgainstPaymentAt,
        contractIds: cid,
        preview,
        index,
      })
        .then((res) => {
          const { data, errors } = res.data as {
            data: string
            errors: string[]
          }
          // 何もない時
          if (errors.length === 0 && data === "") {
            results.errors.push(`contract_id: ${cid}, データは存在していません`)
          } else if (errors.length > 0) {
            errors.map((error) => results.errors.push(error))
          } else {
            // 成功例
            const repData = data.replace(/N2402300000002/g, "")
            results.data.push(`N${2402300000001 + i}${repData}`)
            i += 1
          }
        })
        .catch((err) => {
          console.log({ err })

          // プログラムエラー
          if (formState.mode === "register") {
            results.errors.push(`contract_id: ${cid}, ${err.message}`)
          } else {
            // こっちのエラーケースは不明
          }
        })
        .finally(() => {
          setFetchCount((count) => count + 1)
        })
    }

    // CSV吐き出しでも結果を出力するように変更
    setPreviewJson(results)

    // 吐き出し部分のみ外出し
    if (!payload.preview) {
      const csvString = results.data.join("")
      const utf8Array = new TextEncoder().encode(csvString)
      const data = convert(utf8Array, {
        to: "SJIS",
        from: "UTF8",
      })
      const blob = new Blob([new Uint8Array(data)], { type: "text/csv;charset=shift_jis;" })
      const url = URL || webkitURL
      anchorRef.current.href = url.createObjectURL(blob)
      anchorRef.current.download = `${dayjs().format("YYY-MM-DD_HHmm")}.csv`
      anchorRef.current.click()
    }
  }

  return (
    <>
      <a href={null} download ref={anchorRef}></a>
      <StyledComponent
        previewJson={previewJson}
        formState={formState}
        // 一リクエストで処理
        // handleSubmit={handleSubmit}
        // ここから↓が個別取得で追加したパラメータ
        handleSubmit={splitTransmission}
        setFormState={setFormState}
        fetchCount={fetchCount}
        totalCount={totalCount}
        estimateRemain={estimateRemain}
      />
    </>
  )
}
